import React, { useState } from 'react';
import { useCanvas } from '../../../context/CanvasContext';
import styles from './styles/LinkSituation.module.css';

/**
 * Re-points a choice at a different situation.
 *
 * There is no `nextSituation` field to write: the edge's target IS the link, so
 * this dispatches a `setEdgeTarget` op. Pointing a choice back at its own source
 * turns it into a hint, which the op applier derives on both sides.
 *
 * @param {{edgeId: string}} props
 */
const LinkSituation = ({ edgeId }) => {
    const { nodes, getEdge, setChoiceTarget } = useCanvas();
    const [searchTerm, setSearchTerm] = useState('');

    const edge = getEdge(edgeId);
    const currentTargetId = edge?.target || null;

    // An edge always has a target that exists — `removeNode` rewires incoming
    // edges rather than leaving them dangling — but a draft migrated from older
    // data can still arrive pointing at nothing.
    const isTargetMissing = !currentTargetId || !nodes.some((node) => node.id === currentTargetId);

    const filteredNodes = nodes.filter((node) =>
        (node.data?.title || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className={styles.linkSituationContainer}>
            <h2 className={styles.selectedChoiceTitle}>
                {edge?.data?.title || 'No Choice Selected'}
            </h2>

            <h3 className={styles.situationListTitle}>Link to Situation:</h3>

            <div className={styles.searchContainer}>
                <input
                    type="search"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search situations..."
                    className={styles.searchInput}
                    aria-label="Search situations to link"
                />
            </div>

            {isTargetMissing && (
                <p className={styles.warningText}>
                    ⚠️ This choice is currently linked to an unknown or deleted situation. Please
                    select a new one.
                </p>
            )}

            <div className={styles.listWrapper}>
                {filteredNodes.length > 0 ? (
                    <ul className={styles.situationList}>
                        {filteredNodes.map((node) => (
                            <li
                                key={node.id}
                                className={`${styles.situationItem} ${
                                    node.id === currentTargetId ? styles.highlightedSituation : ''
                                }`}
                                onClick={() => {
                                    if (node.id !== currentTargetId) setChoiceTarget(edgeId, node.id);
                                }}
                                role="button"
                                aria-pressed={node.id === currentTargetId}
                            >
                                <span className={styles.situationItemTitle}>
                                    {node.data?.title || 'Untitled Situation'}
                                </span>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className={styles.noResultsText}>
                        {searchTerm
                            ? 'No situations match your search.'
                            : 'No situations available.'}
                    </p>
                )}
            </div>
        </div>
    );
};

export default LinkSituation;
